import React, { createContext, useReducer } from "react";

import { PLAYER_NAMES } from "../constants";

export const OmahaActionTypes = {
  PICK_CARD: "PICK_CARD",
  UNPICK_CARD: "UNPICK_CARD",
  CLEAR_HAND: "CLEAR_HAND",
  SELECT_PLAYER: "SELECT_PLAYER",
  ADD_PLAYER: "ADD_PLAYER",
  REMOVE_PLAYER: "REMOVE_PLAYER",
  UPDATE_BOARD: "UPDATE_BOARD"
};

export const OMAHA_HOLE_CARDS = 4;
export const MIN_PLAYERS = 2;

// A factory, not a module-level constant: every provider gets its own state so
// one mount can't inherit another's hands.
export function createInitialOmahaState() {
  const players = PLAYER_NAMES.slice(0, MIN_PLAYERS);
  const hands = {};
  players.forEach(player => {
    hands[player] = [];
  });
  return { players, hands, selected: players[0], board: "" };
}

export function cardsInPlay(state) {
  const cards = new Set();
  Object.values(state.hands).forEach(hand => hand.forEach(c => cards.add(c)));
  boardCards(state.board).forEach(c => cards.add(c));
  return cards;
}

export function boardCards(board) {
  return board.length >= 2 ? board.match(/.{1,2}/g).filter(c => c.length === 2) : [];
}

export function isComplete(hand) {
  return hand.length === OMAHA_HOLE_CARDS;
}

function omahaReducer(state, action) {
  switch (action.type) {
    case OmahaActionTypes.PICK_CARD: {
      const hand = state.hands[state.selected];
      if (hand.length >= OMAHA_HOLE_CARDS || cardsInPlay(state).has(action.card)) {
        return state;
      }
      return {
        ...state,
        hands: { ...state.hands, [state.selected]: [...hand, action.card] }
      };
    }
    case OmahaActionTypes.UNPICK_CARD: {
      const player = action.player || state.selected;
      return {
        ...state,
        hands: {
          ...state.hands,
          [player]: state.hands[player].filter(c => c !== action.card)
        }
      };
    }
    case OmahaActionTypes.CLEAR_HAND:
      return {
        ...state,
        hands: { ...state.hands, [action.player || state.selected]: [] }
      };
    case OmahaActionTypes.SELECT_PLAYER:
      return state.players.includes(action.player)
        ? { ...state, selected: action.player }
        : state;
    case OmahaActionTypes.ADD_PLAYER: {
      const next = PLAYER_NAMES.find(p => !state.players.includes(p));
      if (!next) {
        return state;
      }
      return {
        ...state,
        players: [...state.players, next],
        hands: { ...state.hands, [next]: [] },
        selected: next
      };
    }
    case OmahaActionTypes.REMOVE_PLAYER: {
      if (state.players.length <= MIN_PLAYERS) {
        return state;
      }
      const players = state.players.filter(p => p !== action.player);
      const hands = { ...state.hands };
      delete hands[action.player];
      return {
        ...state,
        players,
        hands,
        selected: state.selected === action.player ? players[0] : state.selected
      };
    }
    case OmahaActionTypes.UPDATE_BOARD:
      return { ...state, board: action.board };
    default:
      return state;
  }
}

export const OmahaContext = createContext(createInitialOmahaState());

export default function OmahaStore(props) {
  const [state, dispatch] = useReducer(omahaReducer, createInitialOmahaState());
  return (
    <OmahaContext.Provider value={[state, dispatch]}>
      {props.children}
    </OmahaContext.Provider>
  );
}
